// FILE: frontend/src/utils/bgSelection.ts
// Background selection management with cross-tab synchronization

// ──────────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "selected_bg";
const EVENT_NAME = "bg-changed";
const DEFAULT_BG = "DNA_Helix_Motion-vertical.mp4";

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface BackgroundChangeEvent {
  name: string;
  timestamp: number;
  source: "user" | "storage";
}

export type BackgroundChangeHandler = (name: string, event: BackgroundChangeEvent) => void;

// ──────────────────────────────────────────────────────────────────────────────
// Storage Operations
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Get the currently selected background
 * Returns default if none is set or storage is unavailable
 */
export function getSelectedBg(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || DEFAULT_BG;
  } catch (err) {
    console.warn("Failed to get selected background:", err);
    return DEFAULT_BG;
  }
}

/**
 * Set the selected background
 * Stores in localStorage and dispatches event for cross-component sync
 * 
 * @param name - Background filename (e.g., "DNA_Helix_Motion-vertical.mp4")
 */
export function setSelectedBg(name: string): void {
  if (!name || typeof name !== "string") {
    console.warn("Invalid background name:", name);
    return;
  }

  try {
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, name);

    // Dispatch event for same-tab updates
    dispatchBackgroundChangeEvent(name, "user");
  } catch (err) {
    console.warn("Failed to set selected background:", err);
  }
}

/**
 * Clear the selected background (reset to default)
 */
export function clearSelectedBg(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    dispatchBackgroundChangeEvent(DEFAULT_BG, "user");
  } catch (err) {
    console.warn("Failed to clear selected background:", err);
  }
}

/**
 * Check if a specific background is currently selected
 */
export function isBackgroundSelected(name: string): boolean {
  return getSelectedBg() === name;
}

// ──────────────────────────────────────────────────────────────────────────────
// Event System
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Dispatch a background change event
 * @internal
 */
function dispatchBackgroundChangeEvent(name: string, source: "user" | "storage"): void {
  try {
    const event: BackgroundChangeEvent = {
      name,
      timestamp: Date.now(),
      source,
    };

    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: event,
      })
    );
  } catch (err) {
    console.warn("Failed to dispatch background change event:", err);
  }
}

/**
 * Listen to background changes (both same-tab and cross-tab)
 * 
 * @param handler - Function called when background changes
 * @returns Cleanup function to remove listener
 * 
 * @example
 * ```tsx
 * useEffect(() => {
 *   return listenSelectedBg((name) => {
 *     console.log("Background changed to:", name);
 *   });
 * }, []);
 * ```
 */
export function listenSelectedBg(
  handler: ((name: string) => void) | BackgroundChangeHandler
): () => void {
  // Handler for cross-tab changes (storage event)
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      const event: BackgroundChangeEvent = {
        name: e.newValue,
        timestamp: Date.now(),
        source: "storage",
      };

      // Check if handler accepts 2 arguments
      if (handler.length === 2) {
        (handler as BackgroundChangeHandler)(e.newValue, event);
      } else {
        handler(e.newValue, event as any);
      }
    }
  };

  // Handler for same-tab changes (custom event)
  const onCustom = (e: Event) => {
    const customEvent = e as CustomEvent<BackgroundChangeEvent>;
    if (customEvent.detail?.name) {
      // Check if handler accepts 2 arguments
      if (handler.length === 2) {
        (handler as BackgroundChangeHandler)(
          customEvent.detail.name,
          customEvent.detail
        );
      } else {
        handler(customEvent.detail.name, customEvent.detail as any);
      }
    }
  };

  // Add listeners
  window.addEventListener("storage", onStorage);
  window.addEventListener(EVENT_NAME, onCustom);

  // Return cleanup function
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(EVENT_NAME, onCustom);
  };
}

/**
 * Simple listener that only fires for user-initiated changes
 * Ignores cross-tab storage events
 * 
 * @param handler - Function called when user changes background
 * @returns Cleanup function to remove listener
 */
export function listenUserBgChanges(handler: (name: string) => void): () => void {
  const onCustom = (e: Event) => {
    const customEvent = e as CustomEvent<BackgroundChangeEvent>;
    if (customEvent.detail?.name && customEvent.detail.source === "user") {
      handler(customEvent.detail.name);
    }
  };

  window.addEventListener(EVENT_NAME, onCustom);

  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Validation & Utilities
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Validate if a background name is valid
 * Checks if it's a video or image file
 */
export function isValidBackgroundName(name: string): boolean {
  if (!name || typeof name !== "string") return false;

  const validExtensions = [".mp4", ".webm", ".mov", ".jpg", ".jpeg", ".png", ".webp"];
  return validExtensions.some((ext) => name.toLowerCase().endsWith(ext));
}

/**
 * Get the background type from filename
 */
export function getBackgroundType(name: string): "video" | "image" | "unknown" {
  if (!name) return "unknown";

  const videoExtensions = [".mp4", ".webm", ".mov"];
  const imageExtensions = [".jpg", ".jpeg", ".png", ".webp"];

  const lower = name.toLowerCase();

  if (videoExtensions.some((ext) => lower.endsWith(ext))) return "video";
  if (imageExtensions.some((ext) => lower.endsWith(ext))) return "image";

  return "unknown";
}

/**
 * Get background URL from name
 * Constructs full path for use in components
 */
export function getBackgroundUrl(name?: string): string {
  const bgName = name || getSelectedBg();
  return `/static/backgrounds/${bgName}`;
}

/**
 * Check if background is a video
 */
export function isVideoBackground(name?: string): boolean {
  const bgName = name || getSelectedBg();
  return getBackgroundType(bgName) === "video";
}

// ──────────────────────────────────────────────────────────────────────────────
// Debug Utilities
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Get background selection debug info
 */
export function getBackgroundDebugInfo() {
  return {
    selected: getSelectedBg(),
    default: DEFAULT_BG,
    type: getBackgroundType(getSelectedBg()),
    url: getBackgroundUrl(),
    isVideo: isVideoBackground(),
    storageKey: STORAGE_KEY,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Exports for backward compatibility
// ──────────────────────────────────────────────────────────────────────────────

export const SELECTED_BG_KEY = STORAGE_KEY; // Deprecated: use getSelectedBg() instead
export { DEFAULT_BG };
